((root, factory) => {
  const engine = factory();

  if (typeof module === 'object' && module.exports) module.exports = engine;
  if (root) root.NovvaPriceEngine = Object.freeze(engine);
})(typeof globalThis !== 'undefined' ? globalThis : this, () => {
  const invalid = (errors, details = {}) => ({ status: 'invalid', errors, ...details });

  const normalizeRollWidths = (rollWidthsCm) =>
    [...new Set((Array.isArray(rollWidthsCm) ? rollWidthsCm : []).map(Number))]
      .filter((width) => Number.isInteger(width) && width > 0)
      .sort((a, b) => a - b);

  /**
   * Returns the first available roll width that can produce the requested cut.
   * Requests narrower than the smallest roll are accepted and billed at the
   * smallest roll width. Requests wider than the largest roll are invalid.
   *
   * @param {number} requestedWidthCm
   * @param {number[]} rollWidthsCm
   * @returns {number|null}
   */
  const nextRollWidth = (requestedWidthCm, rollWidthsCm) =>
    normalizeRollWidths(rollWidthsCm).find((rollWidth) => rollWidth >= requestedWidthCm) ?? null;

  /**
   * Stable adapter boundary for Novva dimension pricing.
   * The calculated storefront quote is only informational until the Shopify
   * price provider confirms the same amount in cart and checkout.
   *
   * @param {{
   *   mode: 'standard'|'custom',
   *   widthCm: number|null,
   *   lengthCm: number|null,
   *   unitPriceCents: number|null,
   *   currency: string,
   *   roundingPolicy: {
   *     type?: 'roll_width_step_up',
   *     rollWidthsCm: number[],
   *     maxLengthCm?: number
   *   }|null
   * }} input
   * @returns {object}
   */
  const quoteSize = (input) => {
    if (!['standard', 'custom'].includes(input?.mode)) return invalid(['invalid_mode']);
    if (input.mode === 'standard') return { status: 'pending', reason: 'standard_variant_pricing' };

    const errors = [];
    if (!Number.isInteger(input.widthCm) || input.widthCm <= 0) errors.push('invalid_width');
    if (!Number.isInteger(input.lengthCm) || input.lengthCm <= 0) errors.push('invalid_length');
    if (errors.length) return invalid(errors);

    if (!input.roundingPolicy) return { status: 'pending', reason: 'rules_missing' };

    const rollWidthsCm = normalizeRollWidths(input.roundingPolicy.rollWidthsCm);
    if (!rollWidthsCm.length) return { status: 'pending', reason: 'roll_widths_missing' };

    const maxLengthCm = Number(input.roundingPolicy.maxLengthCm) || 3000;
    const maxWidthCm = rollWidthsCm.at(-1);

    if (input.widthCm > maxWidthCm) {
      errors.push('width_above_max');
    }
    if (input.lengthCm > maxLengthCm) {
      errors.push('length_above_max');
    }
    if (errors.length) return invalid(errors, { maxWidthCm, maxLengthCm });

    if (!Number.isInteger(input.unitPriceCents) || input.unitPriceCents <= 0) {
      return { status: 'pending', reason: 'unit_price_missing' };
    }

    const billableWidthCm = nextRollWidth(input.widthCm, rollWidthsCm);
    if (!billableWidthCm) return invalid(['width_above_max'], { maxWidthCm, maxLengthCm });

    const billableLengthCm = input.lengthCm;
    const billableAreaSquareCm = billableWidthCm * billableLengthCm;
    const billableAreaM2 = billableAreaSquareCm / 10000;
    const totalPriceCents = Math.round((billableAreaSquareCm * input.unitPriceCents) / 10000);

    return {
      status: 'ready',
      requestedWidthCm: input.widthCm,
      requestedLengthCm: input.lengthCm,
      billableWidthCm,
      billableLengthCm,
      billableAreaM2,
      unitPriceCents: input.unitPriceCents,
      totalPriceCents,
      currency: input.currency || 'TRY',
    };
  };

  return { nextRollWidth, normalizeRollWidths, quoteSize };
});
